import { useCallback, useMemo, useRef, useState } from "react";
import type { OnboardingAnswers, StepId } from "../types/onboarding";
import { canDoStatements, placementQuiz } from "../data/onboarding";
import { computePlacement, quizFinished, suggestedFocus } from "../utils/placement";

const initialAnswers: OnboardingAnswers = {
  base: "en",
  target: undefined,
  targets: [],
  otherLevels: {},
  goals: [],
  selfLevel: undefined,
  canDo: [],
  quiz: [],
  focus: [],
  speaking: true,
  minutes: 10,
  reminder: "evening",
};

/** Beginners skip the skill check; the quick quiz only runs where we have content for the target. */
export function buildSteps(a: OnboardingAnswers): StepId[] {
  const steps: StepId[] = ["welcome", "languages", "goals", "level"];
  if (a.selfLevel !== undefined && a.selfLevel > 0) {
    steps.push("cando");
    if (a.target && placementQuiz[a.target]) steps.push("quiz");
  }
  steps.push("focus", "schedule", "plan");
  return steps;
}

export function useOnboarding() {
  const [answers, setAnswers] = useState<OnboardingAnswers>(initialAnswers);
  const [step, setStep] = useState<StepId>("welcome");
  const [direction, setDirection] = useState<1 | -1>(1);
  const [focusTouched, setFocusTouched] = useState(false);

  const steps = useMemo(() => buildSteps(answers), [answers]);
  const index = Math.max(0, steps.indexOf(step));
  const placement = useMemo(() => computePlacement(answers), [answers]);
  const quizItems = answers.target ? placementQuiz[answers.target] : undefined;

  const update = useCallback((patch: Partial<OnboardingAnswers>) => setAnswers((a) => ({ ...a, ...patch })), []);

  const canContinue = (() => {
    switch (step) {
      case "languages":
        return Boolean(answers.target);
      case "goals":
        return answers.goals.length > 0;
      case "level":
        return answers.selfLevel !== undefined;
      case "cando":
        return answers.canDo.filter((v) => v !== undefined).length === canDoStatements.length;
      case "quiz":
        return quizItems ? quizFinished(answers.quiz, quizItems.length) : true;
      case "focus":
        return answers.focus.length > 0;
      default:
        return true;
    }
  })();

  // Steps may call next() from a timeout after updating answers, so always read the latest values.
  const latest = useRef({ answers, step, focusTouched });
  latest.current = { answers, step, focusTouched };

  const next = useCallback(() => {
    const { answers: a, step: current, focusTouched: touched } = latest.current;
    // Smart default: pre-pick focus skills from goals until the learner edits them.
    if (current === "goals" && !touched) {
      setAnswers((prev) => ({ ...prev, focus: suggestedFocus(prev.goals) }));
    }
    const list = buildSteps(a);
    setDirection(1);
    setStep(list[Math.min(list.indexOf(current) + 1, list.length - 1)]);
  }, []);

  const back = useCallback(() => {
    const { answers: a, step: current } = latest.current;
    const list = buildSteps(a);
    setDirection(-1);
    setStep(list[Math.max(list.indexOf(current) - 1, 0)]);
  }, []);

  const goTo = useCallback((id: StepId) => {
    setDirection(-1);
    if (id === "quiz") setAnswers((a) => ({ ...a, quiz: [] }));
    setStep(id);
  }, []);

  return {
    answers,
    update,
    step,
    steps,
    index,
    direction,
    placement,
    quizItems,
    canContinue,
    next,
    back,
    goTo,
    markFocusTouched: () => setFocusTouched(true),
    /** 0–100, excluding the welcome screen. */
    progress: step === "welcome" ? 0 : (index / (steps.length - 1)) * 100,
  };
}
